import { useEffect, useState, type ReactNode } from 'react';
import { Wrench, MessageCircle, X, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  FILTER_BY_SECTION_ID,
  RAIL_FILTER_EVENT,
  RAIL_FILTER_LABELS,
  RailFilterContext,
  type RailFilterId,
} from './railFilter';

export type RailTab = 'tools' | 'comments';

export interface EditorRailProps {
  /** Active tab. Optional — when omitted the rail manages its own state. */
  tab?: RailTab;
  /** Default tab when uncontrolled. */
  defaultTab?: RailTab;
  /**
   * Setter for the active tab. Either form works; both are wired the same way.
   * `setTab(next)` matches the design package; `onTabChange(next)` matches the
   * standard React controlled-component naming. Provide one, not both.
   */
  setTab?: (next: RailTab) => void;
  onTabChange?: (next: RailTab) => void;
  /** Tools tab body — usually a stack of cards. */
  tools: ReactNode;
  /** Comments tab body. */
  comments: ReactNode;
  /** Optional unread badges per tab. */
  badges?: Partial<Record<RailTab, number>>;
  className?: string;
}

interface TabDef {
  id: RailTab;
  label: string;
  icon: LucideIcon;
}

const TABS: TabDef[] = [
  { id: 'tools', label: 'Tools', icon: Wrench },
  { id: 'comments', label: 'Comments', icon: MessageCircle },
];

const MINI_RAIL_JUMP_EVENT = 'ed-mini-rail-jump';
const RAIL_SCROLL_OFFSET = 12;
const PAGE_SCROLL_OFFSET = 96;
const RAIL_SCROLL_BEHAVIOR = 'instant' as ScrollBehavior;

/**
 * 3-tab rail container. Tools is scrollable card stack; Comments and Ask AI
 * are full-height regions that own their own internal scroll. The active tab's
 * panel is mounted with `display:` toggling so internal scroll position and
 * draft state survive tab switches.
 */
export function EditorRail({
  tab,
  defaultTab = 'tools',
  setTab,
  onTabChange,
  tools,
  comments,
  badges,
  className,
}: EditorRailProps) {
  const isControlled = tab !== undefined;
  const [internalTab, setInternalTab] = useState<RailTab>(defaultTab);
  const active = isControlled ? tab : internalTab;
  // Mini-rail section filter for the Tools stack. null = show every card.
  const [sectionFilter, setSectionFilter] = useState<RailFilterId | null>(null);

  const notify = (next: RailTab) => {
    setTab?.(next);
    onTabChange?.(next);
  };

  const focusTools = () => {
    if (!isControlled) setInternalTab('tools');
    notify('tools');
  };

  // Announce the active filter so the mini-rail can highlight its cell, and
  // start a freshly-filtered view at the top of the panel.
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent(RAIL_FILTER_EVENT, { detail: { filter: sectionFilter } }),
    );
    const panel = document.getElementById('rail-panel-tools');
    if (panel) panel.scrollTop = 0;
  }, [sectionFilter]);

  const scrollToRailSection = (sectionId: string) => {
    window.setTimeout(() => {
      const panel = document.getElementById('rail-panel-tools');
      const target = panel?.querySelector<HTMLElement>(
        `[data-rail-section="${sectionId}"]`,
      );
      if (!panel || !target) return;

      const panelRect = panel.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const nextTop = panel.scrollTop + targetRect.top - panelRect.top - RAIL_SCROLL_OFFSET;

      if (panel.scrollHeight > panel.clientHeight + 1) {
        panel.scrollTo({ top: Math.max(0, nextTop), behavior: RAIL_SCROLL_BEHAVIOR });
        return;
      }

      const pageTop = window.scrollY + targetRect.top - PAGE_SCROLL_OFFSET;
      window.scrollTo({ top: Math.max(0, pageTop), behavior: RAIL_SCROLL_BEHAVIOR });
    }, 0);
  };

  // Listen for the score-badge → quality-card jump event so the rail snaps to Tools.
  useEffect(() => {
    const onFocusQuality = () => {
      focusTools();
      setSectionFilter('quality');
      scrollToRailSection('quality');
    };
    window.addEventListener('ed-focus-quality', onFocusQuality);
    return () => window.removeEventListener('ed-focus-quality', onFocusQuality);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isControlled, setTab, onTabChange]);

  useEffect(() => {
    const onMiniRailJump = (event: Event) => {
      const detail = (event as CustomEvent<{ sectionId?: string; itemId?: RailFilterId }>)
        .detail;
      if (!detail) return;

      focusTools();

      // Mini-rail cell click: FILTER to that group; clicking the active cell
      // again clears the filter back to the full stack.
      if (detail.itemId) {
        setSectionFilter((prev) => (prev === detail.itemId ? null : detail.itemId!));
        return;
      }

      // Card-id jump (issue-badge field cursor, legacy dispatchers): focus the
      // owning group so the card is visible, then scroll to the exact card —
      // groups like Meta hold several cards.
      if (detail.sectionId) {
        setSectionFilter(FILTER_BY_SECTION_ID[detail.sectionId] ?? null);
        scrollToRailSection(detail.sectionId);
      }
    };

    window.addEventListener(MINI_RAIL_JUMP_EVENT, onMiniRailJump);
    return () => window.removeEventListener(MINI_RAIL_JUMP_EVENT, onMiniRailJump);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isControlled, setTab, onTabChange]);

  const handleTab = (next: RailTab) => {
    if (!isControlled) setInternalTab(next);
    notify(next);
  };

  return (
    <aside
      data-component="editor-rail"
      className={cn(
        'flex flex-col h-full min-h-0 w-full bg-warm-bg border-l border-border',
        className,
      )}
      aria-label="Editor side panel"
    >
      <div role="tablist" className="flex border-b border-border bg-card shrink-0">
        {TABS.map(({ id, label, icon: Icon }) => {
          const isActive = active === id;
          const badge = badges?.[id];
          return (
            <button
              key={id}
              role="tab"
              type="button"
              aria-selected={isActive}
              aria-controls={`rail-panel-${id}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => handleTab(id)}
              className={cn(
                'flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-[12px] font-semibold border-b-2 cursor-pointer transition-colors',
                isActive
                  ? 'border-coral text-coral-dark bg-coral-light/40'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-warm-bg',
              )}
            >
              <Icon className="size-3.5" />
              <span>{label}</span>
              {badge != null && badge > 0 && (
                <span className="ml-0.5 inline-flex items-center justify-center min-w-4 h-4 rounded-full bg-coral text-white text-[10px] font-bold px-1">
                  {badge > 99 ? '99+' : badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Section-filter chip — visible while the mini-rail narrows the stack. */}
      {sectionFilter !== null && active === 'tools' && (
        <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border bg-coral-light/30 shrink-0">
          <span className="text-[11px] font-semibold text-coral-dark">
            Showing: {RAIL_FILTER_LABELS[sectionFilter]}
          </span>
          <button
            type="button"
            onClick={() => setSectionFilter(null)}
            className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium text-muted-foreground bg-transparent border-none cursor-pointer hover:text-foreground hover:bg-warm-bg transition-colors"
          >
            <X className="size-3" />
            Show all
          </button>
        </div>
      )}

      {/* Panels — kept mounted so scroll/draft state survives tab switches. */}
      <div
        role="tabpanel"
        id="rail-panel-tools"
        aria-hidden={active !== 'tools'}
        className={cn(
          'flex-1 min-h-0 overflow-y-auto p-3 flex flex-col gap-3',
          active !== 'tools' && 'hidden',
        )}
      >
        <RailFilterContext.Provider value={sectionFilter}>
          {tools}
        </RailFilterContext.Provider>
      </div>
      <div
        role="tabpanel"
        id="rail-panel-comments"
        aria-hidden={active !== 'comments'}
        className={cn('flex-1 min-h-0', active !== 'comments' && 'hidden')}
      >
        {comments}
      </div>
    </aside>
  );
}
